import ky, { Options } from 'ky'

import { authUserApi } from '@api'

import Config from '../config'
import { AuthService } from './AuthService'

const apiHost = Config.apiHost

export interface ApiServiceGetResponse<T> {
  error?: string
  error_code?: string
  metadata?: { [key: string]: any }
  data?: T
  ok: boolean
}

export interface ApiServiceResponse {
  error?: string
  metadata?: { [key: string]: any }
  data?: any
  ok: boolean
  [key: string]: any
}

export type ApiPostRequestParams = {
  endpoint: string
  options?: { [key: string]: any }
  data?: { [key: string]: any } | string
  formData?: FormData | { [key: string]: any } | string
}

const api = ky.extend({
  hooks: {
    beforeRequest: [
      (request) => {
        const { accessToken } = AuthService.getCredentials()
        if (accessToken) {
          request.headers.set('Authorization', `Bearer ${accessToken}`)
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        if (response.status === 401) {
          const refreshed = await ApiService.after401()

          if (refreshed) {
            const { accessToken } = AuthService.getCredentials()
            request.headers.set('Authorization', `Bearer ${accessToken}`)
            return ky(request, options)
          }
        }

        return response
      },
    ],
  },
})

export const ApiService = {
  after401: async (): Promise<boolean> => {
    try {
      const { ok, data: accessToken } = await authUserApi()

      if (ok && accessToken) {
        AuthService.setCredentials({ accessToken })

        return true
      }

      AuthService.clearCredentials()

      return false
    } catch (err) {
      AuthService.clearCredentials()

      return false
    }
  },
  get: async <T = any>({
    endpoint,
    options,
    host,
    signal,
  }: {
    endpoint: string
    options?: { [key: string]: any }
    host?: string
    signal?: AbortSignal
  }): Promise<ApiServiceGetResponse<T>> => {
    const request = api.get(`${host ? host : apiHost}${endpoint}`, {
      searchParams: options,
      signal,
      // retry: 3,
    })

    return new Promise(async (resolve, reject) => {
      const response = await request.json().catch((err) => {
        resolve({ ok: false })
      })

      resolve(response as any)
    })
  },

  post: async ({
    endpoint,
    options,
    data,
    formData,
  }: ApiPostRequestParams): Promise<ApiServiceResponse> => {
    return new Promise(async (resolve, reject) => {
      const params: Options = {
        ...options,
      }

      if (data) {
        params.json = data
      }

      if (formData) {
        params.body = formData as any
      }
      const response = await api
        .post(`${apiHost}${endpoint}`, params)
        .json()
        .catch((err) => {
          // console.log(
          //   1,
          //   err,
          //   err.name,
          //   err.message,
          //   err.statusCode,
          //   err.errorMessage,
          //   err.statusText
          // )

          resolve({ ok: false, data: null })
        })
      resolve(response as any)
    })
  },

  delete: async ({
    endpoint,
  }: {
    endpoint: string
  }): Promise<ApiServiceResponse> => {
    return await api.delete(`${apiHost}${endpoint}`).json()
  },

  postWithBodyResponse: async ({
    endpoint,
    options,
    data,
    formData,
  }: {
    endpoint: string
    options?: { [key: string]: any }
    data?: { [key: string]: any }
    formData?: FormData | { [key: string]: any } | string
  }): Promise<ApiServiceResponse> => {
    return new Promise(async (resolve, reject) => {
      const params = {
        ...options,
      }

      if (data) {
        params.json = data
      }

      if (formData) {
        params.body = formData
      }

      params.throwHttpErrors = false

      const response = await api.post(`${apiHost}${endpoint}`, params).json()

      return resolve(response as any)
    })
  },

  json: async ({ endpoint }: { endpoint: string }): Promise<any> => {
    return await ky(endpoint, {
      fetch,
    }).json()
  },

  patch: async ({
    endpoint,
    data,
  }: {
    endpoint: string
    data?: { [key: string]: any }
  }): Promise<ApiServiceResponse> => {
    return new Promise(async (resolve, reject) => {
      const params = {
        json: data,
      }

      const response = await api
        .patch(`${apiHost}${endpoint}`, params)
        .json()
        .catch((err) => {
          resolve({ ok: false, data: null })
        })

      resolve(response as any)
    })
  },

  poll: async function <T>(
    fn: () => Promise<T>,
    validate: (result: T) => boolean,
    interval = 1000,
    maxAttempts = 10
  ): Promise<T> {
    let attempts = 0
    return new Promise((resolve, reject) => {
      const executePoll = async () => {
        attempts++
        try {
          const result = await fn()
          if (validate(result)) {
            return resolve(result)
          } else if (attempts >= maxAttempts) {
            return reject(new Error('Max attempts reached'))
          } else {
            setTimeout(executePoll, interval)
          }
        } catch (err) {
          reject(err)
        }
      }
      executePoll()
    })
  },

  pollGet: async function <T>(
    endpoint: string,
    validate: (result: ApiServiceGetResponse<T>) => boolean,
    options?: { [key: string]: any },
    interval = 1000,
    maxAttempts = 10
  ): Promise<ApiServiceGetResponse<T>> {
    return this.poll<ApiServiceGetResponse<T>>(
      () => this.get<T>({ endpoint, options }),
      validate,
      interval,
      maxAttempts
    )
  },
}
